/**
 * Thin wrapper over window.Telegram.WebApp.
 *
 * Everything is optional-chained because the app must also run in a plain
 * browser tab during development — rebuilding and reopening Telegram for
 * every CSS tweak is not a workflow. Set VITE_DEV_INIT_DATA (from the
 * backend's `npm run fake-init`) to authenticate outside Telegram.
 */

interface MainButton {
  setText(t: string): MainButton;
  show(): MainButton;
  hide(): MainButton;
  enable(): MainButton;
  disable(): MainButton;
  showProgress(leaveActive?: boolean): MainButton;
  hideProgress(): MainButton;
  onClick(cb: () => void): MainButton;
  offClick(cb: () => void): MainButton;
  setParams(p: Record<string, unknown>): MainButton;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { user?: { id: number; first_name: string; last_name?: string; username?: string; language_code?: string }; start_param?: string };
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  viewportStableHeight: number;
  MainButton: MainButton;
  BackButton: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  HapticFeedback: {
    impactOccurred(style: 'light' | 'medium' | 'heavy'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  ready(): void;
  expand(): void;
  close(): void;
  showAlert(msg: string, cb?: () => void): void;
  showConfirm(msg: string, cb: (ok: boolean) => void): void;
  showPopup(
    params: { title?: string; message: string; buttons?: { id?: string; type?: string; text?: string }[] },
    cb?: (buttonId: string) => void,
  ): void;
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
  setHeaderColor(color: string): void;
  enableClosingConfirmation(): void;
}

export const tg: TelegramWebApp | undefined = (window as any).Telegram?.WebApp;

export const inTelegram = Boolean(tg?.initData);

export function initTelegram() {
  if (!tg) return;
  tg.ready();
  tg.expand();
  tg.setHeaderColor?.('secondary_bg_color');
}

/** The deep-link payload the app was opened with (t.me/<bot>?startapp=…), if any. */
export function startParam(): string | null {
  if (tg?.initDataUnsafe?.start_param) return tg.initDataUnsafe.start_param;
  try { return new URLSearchParams(getInitData()).get('start_param'); } catch { return null; }
}

/** initData for the auth handshake, or a dev-mode substitute. */
export function getInitData(): string {
  if (tg?.initData) return tg.initData;
  return import.meta.env.VITE_DEV_INIT_DATA ?? '';
}

export const haptic = {
  tap: () => tg?.HapticFeedback?.selectionChanged(),
  press: () => tg?.HapticFeedback?.impactOccurred('light'),
  success: () => tg?.HapticFeedback?.notificationOccurred('success'),
  error: () => tg?.HapticFeedback?.notificationOccurred('error'),
};

/**
 * Telegram's native bottom button. Using it instead of a custom CTA means
 * the primary action sits exactly where the platform puts it, above the
 * keyboard, styled by the user's own theme.
 */
/**
 * Telegram's native bottom button.
 *
 * The handler must be tracked at module level. offClick() needs the SAME
 * function reference that was passed to onClick(), and the caller creates
 * a fresh closure every render — so calling offClick with the new one
 * removes nothing and the old handlers stay attached.
 *
 * The result was that after two bookings, one tap fired both handlers:
 * the current one booked correctly while a stale closure retried the
 * previous slot, which was now taken. A successful booking and a
 * "no longer open" error, at the same time.
 */
let lastHandler: (() => void) | null = null;

export function useMainButton() {
  return {
    show(text: string, onClick: () => void) {
      if (!tg) return;
      tg.MainButton.setText(text);
      if (lastHandler) tg.MainButton.offClick(lastHandler);
      lastHandler = onClick;
      tg.MainButton.onClick(onClick);
      tg.MainButton.enable();
      tg.MainButton.show();
    },
    busy() {
      tg?.MainButton.showProgress(false);
    },
    idle() {
      tg?.MainButton.hideProgress();
      tg?.MainButton.enable();
    },
    hide() {
      if (tg && lastHandler) {
        tg.MainButton.offClick(lastHandler);
        lastHandler = null;
      }
      tg?.MainButton.hideProgress();
      tg?.MainButton.hide();
    },
  };
}

/**
 * Open a link outside Telegram.
 *
 * Telegram's WebView renders a .ics file as text rather than handing it
 * to the OS, so "add to calendar" shows a screen of code. Bounce out to
 * the system browser and the phone treats the file properly.
 */
export function openExternal(url: string) {
  const w = tg as any;
  if (w?.openLink) w.openLink(url, { try_instant_view: false });
  else window.open(url, '_blank');
}

/**
 * Telegram's own dialogs arrived in 6.2, and plenty of people are on
 * older clients. Calling one there throws WebAppMethodUnsupported, which
 * used to mean a cancel confirmation or a booking result simply never
 * appeared — the tap looked like it had done nothing.
 *
 * Asked before every call rather than once at load: a webview can be
 * reloaded into a different client, and the check is free.
 */
export const canUseDialogs = () => Boolean((tg as any)?.isVersionAtLeast?.('6.2'));

/**
 * Telegram's swipe-down-to-close (7.7+): off for the whole app (lib/nav.ts).
 * Older clients have neither the gesture nor the method.
 */
export function setVerticalSwipes(on: boolean) {
  const w = tg as any;
  if (!w?.isVersionAtLeast?.('7.7')) return;
  try { on ? w.enableVerticalSwipes() : w.disableVerticalSwipes(); } catch { /* unsupported: nothing to undo */ }
}

export function confirm(message: string): Promise<boolean> {
  if (!tg || !canUseDialogs()) return Promise.resolve(window.confirm(message));
  return new Promise((resolve) => {
    try { tg.showConfirm(message, resolve); }
    catch { resolve(window.confirm(message)); }
  });
}

export function alert(message: string): Promise<void> {
  if (!tg || !canUseDialogs()) {
    window.alert(message);
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    try { tg.showAlert(message, () => resolve()); }
    catch { window.alert(message); resolve(); }
  });
}
